import { useState, useEffect } from 'react'
import { useAuthContext } from '../contexts'
import { authService } from '../services/auth'
import type { AuthUser, UserRole, UseAuthReturn } from '../types/auth'

/**
 * Multi-role authentication hook for Phase 3
 *
 * Supports all 4 Phase 3 roles:
 * - ADMIN (role_id: 1)
 * - PARTNER (role_id: 2)
 * - CUSTOMER (role_id: 3)
 * - GUEST (role_id: 4)
 *
 * Role Detection Priority:
 * 1. user_metadata.role (string: 'admin', 'ADMIN', 'partner', etc.)
 * 2. user_metadata.roleId (number: 1, 2, 3, 4)
 * 3. app_metadata.role (string)
 * 4. app_metadata.roleId (number)
 * 5. app_metadata.userId === '1' (legacy admin check)
 * 6. Default to 'CUSTOMER' if authenticated but no role found
 *
 * @example
 * ```tsx
 * const { user, role, isAdmin, isPartner, loading } = useAuth()
 *
 * if (loading) return <Spinner />
 * if (!user) return <LoginPage />
 *
 * if (isAdmin()) {
 *   return <AdminDashboard />
 * }
 *
 * if (isPartner()) {
 *   return <PartnerDashboard />
 * }
 * ```
 */
export function useAuth(): UseAuthReturn {
  const { email, isAuthorized } = useAuthContext()
  const [user, setUser] = useState<AuthUser | null>(null)
  const [role, setRole] = useState<UserRole | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const fetchAuthState = async () => {
      // Keep loading until we know the auth state
      if (isAuthorized === undefined) {
        setLoading(true)
        return
      }

      // User is not authorized
      if (!isAuthorized || !email) {
        setUser(null)
        setRole(null)
        setLoading(false)
        return
      }

      // User is authorized - fetch full session details
      setLoading(true)
      try {
        const session = await authService.getSession()
        const authUser = session?.user as AuthUser

        if (!authUser) {
          setUser(null)
          setRole(null)
          setError(new Error('No user found in session'))
          setLoading(false)
          return
        }

        // Extract role from user metadata with comprehensive fallback
        const detectedRole = extractUserRole(authUser)

        // Set user with extracted role
        const userWithRole: AuthUser = {
          ...authUser,
          role: detectedRole,
          roleId: roleToRoleId(detectedRole),
        }

        setUser(userWithRole)
        setRole(detectedRole)
        setError(null)
      } catch (err) {
        console.error('Error fetching auth state:', err)
        setError(err instanceof Error ? err : new Error('Unknown auth error'))
        setUser(null)
        setRole(null)
      } finally {
        setLoading(false)
      }
    }

    fetchAuthState()
  }, [email, isAuthorized])

  // Role checking helper functions
  const isAdmin = (): boolean => role === 'ADMIN'
  const isPartner = (): boolean => role === 'PARTNER'
  const isCustomer = (): boolean => role === 'CUSTOMER'
  const isGuest = (): boolean => role === 'GUEST'

  const hasRole = (checkRole: UserRole): boolean => role === checkRole

  const hasAnyRole = (roles: UserRole[]): boolean => {
    if (!role) return false
    return roles.includes(role)
  }

  return {
    user,
    role,
    loading,
    error,
    isAdmin,
    isPartner,
    isCustomer,
    isGuest,
    hasRole,
    hasAnyRole,
  }
}

/**
 * Extract user role from Supabase auth user object
 * Handles multiple possible locations for role data
 */
function extractUserRole(authUser: AuthUser): UserRole {
  // Priority 1: user_metadata.role (string)
  if (authUser.user_metadata?.role) {
    const normalized = normalizeRoleName(authUser.user_metadata.role)
    if (normalized) return normalized
  }

  // Priority 2: user_metadata.roleId (number)
  if (typeof authUser.user_metadata?.roleId === 'number') {
    const normalized = roleIdToRole(authUser.user_metadata.roleId)
    if (normalized) return normalized
  }

  // Priority 3: app_metadata.role (string)
  if (authUser.app_metadata?.role) {
    const normalized = normalizeRoleName(authUser.app_metadata.role)
    if (normalized) return normalized
  }

  // Priority 4: app_metadata.roleId (number)
  if (typeof authUser.app_metadata?.roleId === 'number') {
    const normalized = roleIdToRole(authUser.app_metadata.roleId)
    if (normalized) return normalized
  }

  // Priority 5: Legacy admin check (userId === '1')
  if (authUser.app_metadata?.userId === '1') {
    return 'ADMIN'
  }

  // Priority 6: Default to CUSTOMER for authenticated users
  console.warn('⚠️ No role found in user metadata, defaulting to CUSTOMER')
  return 'CUSTOMER'
}

/**
 * Normalize role string to UserRole enum
 * Handles: 'admin', 'ADMIN', 'Admin', 'partner', 'PARTNER', 'customer', 'CUSTOMER', etc.
 */
function normalizeRoleName(role: string): UserRole | null {
  const upperRole = role.toUpperCase()
  if (upperRole === 'ADMIN') return 'ADMIN'
  if (upperRole === 'PARTNER') return 'PARTNER'
  if (upperRole === 'CUSTOMER') return 'CUSTOMER'
  if (upperRole === 'GUEST') return 'GUEST'
  return null
}

/**
 * Convert role_id to UserRole
 * Matches backend: ADMIN=1, PARTNER=2, CUSTOMER=3, GUEST=4
 */
function roleIdToRole(roleId: number): UserRole | null {
  const ROLE_ID_TO_NAME: Record<number, UserRole> = {
    1: 'ADMIN',
    2: 'PARTNER',
    3: 'CUSTOMER',
    4: 'GUEST',
  }
  return ROLE_ID_TO_NAME[roleId] || null
}

/**
 * Convert UserRole to role_id
 */
function roleToRoleId(role: UserRole): number {
  const ROLE_IDS: Record<UserRole, number> = {
    ADMIN: 1,
    PARTNER: 2,
    CUSTOMER: 3,
    GUEST: 4,
  }
  return ROLE_IDS[role]
}
